/**
 * Health Registry - Central registry for health checks.
 *
 * Modules register health checks during init(). The registry aggregates
 * results for the /api/ready endpoint (K8s readiness probe).
 *
 * Built-in check: database connectivity (SELECT 1).
 * Extensible: any module can register additional checks via ctx.services.get('health').
 */

export type HealthStatus = 'ok' | 'degraded' | 'error'

export interface HealthCheckResult {
  status: HealthStatus
  detail?: string
}

export interface HealthCheck {
  name: string
  check: () => Promise<HealthCheckResult>
  /** Timeout in ms. Defaults to DEFAULT_TIMEOUT (5000ms). */
  timeout?: number
}

const DEFAULT_TIMEOUT = 5000

export interface HealthCheckReport {
  status: HealthStatus
  checks: Record<string, { status: HealthStatus; detail?: string; latency_ms: number }>
  timestamp: string
}

export class HealthRegistry {
  private checks = new Map<string, HealthCheck>()

  register(check: HealthCheck): void {
    this.checks.set(check.name, check)
  }

  unregister(name: string): void {
    this.checks.delete(name)
  }

  async checkAll(): Promise<HealthCheckReport> {
    const results: HealthCheckReport['checks'] = {}
    let overallStatus: HealthStatus = 'ok'

    const entries = Array.from(this.checks.values())

    // Run all checks in parallel
    const settled = await Promise.allSettled(
      entries.map(async (hc) => {
        const start = performance.now()
        const timeoutMs = hc.timeout ?? DEFAULT_TIMEOUT
        try {
          const result = await Promise.race([
            hc.check(),
            new Promise<never>((_, reject) => {
              const timer = setTimeout(
                () => reject(new Error(`Health check '${hc.name}' timeout after ${timeoutMs}ms`)),
                timeoutMs
              )
              timer.unref()
            })
          ])
          const latency = Math.round(performance.now() - start)
          return { name: hc.name, ...result, latency_ms: latency }
        } catch (err) {
          const latency = Math.round(performance.now() - start)
          return {
            name: hc.name,
            status: 'error' as HealthStatus,
            detail: err instanceof Error ? err.message : 'Unknown error',
            latency_ms: latency
          }
        }
      })
    )

    for (const result of settled) {
      const value = result.status === 'fulfilled'
        ? result.value
        : { name: 'unknown', status: 'error' as HealthStatus, detail: 'Check rejected', latency_ms: 0 }

      results[value.name] = {
        status: value.status,
        ...(value.detail ? { detail: value.detail } : {}),
        latency_ms: value.latency_ms
      }

      // Aggregate: error > degraded > ok
      if (value.status === 'error') {
        overallStatus = 'error'
      } else if (value.status === 'degraded' && overallStatus !== 'error') {
        overallStatus = 'degraded'
      }
    }

    return {
      status: overallStatus,
      checks: results,
      timestamp: new Date().toISOString()
    }
  }

  /** Get number of registered checks */
  get size(): number {
    return this.checks.size
  }
}

/** Singleton registry instance (created once, shared across modules) */
let registry: HealthRegistry | null = null

export function getHealthRegistry(): HealthRegistry {
  if (!registry) {
    registry = new HealthRegistry()
  }
  return registry
}

/** Reset registry (for testing) */
export function resetHealthRegistry(): void {
  registry = null
}
